import React, { useCallback, useEffect, useState } from "react";
import AdminSidebar from "./Components/MenuSidebar";
import AdminHeader from "../Admin/Components/AdminHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faCheckCircle,
  faChevronRight,
  faCircleXmark,
  faClock,
  faEdit,
  faSearch,
  faSpinner,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { getUserPaymentsListAction } from "../../Services/Store/Actions/BookingsAction";
import { useDispatch, useSelector } from "react-redux";
import { Button, Table } from "react-bootstrap";
import moment from "moment";
import { BookingItemType, PaginationType } from "../../Constants/Interfaces";
import RightSlidingPanel from "../Admin/Components/RightSlidingPanel";
import BookingInfoPanel from "./Components/BookingList/BookingInfoPanel";
import CustomButton from "../../Components/Button";
import DatePicker from "react-datepicker";
import PanelWrapper from "./PanelWrapper";
import { showToast } from "../../utils/helpers";
import ConfirmationModal from "../../Components/ConfirmationModal";
import ActionMenu from "../../Components/ActionMenu";
import Pagination from "../../Components/Pagination";

const MessagesPage = () => {
  const [searchText, setSearchText] = useState("");

  const dispatch = useDispatch();

  const { paymentsList } = useSelector((state: any) => state.bookings);

  const [loading, setLoading] = useState(false);
  const [payments, setPaymentsList] = useState([]);
  const [pagination, setPagination] = useState<PaginationType | null>(null);
  const [filters, setFilters] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null,
  });

  const [rightSidePanel, setRightSidePanel] = useState<{
    show: boolean;
    item: BookingItemType | null;
  }>({
    show: false,
    item: null,
  });

  const onFilterChange = (field: string, value: string) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  const getPaymentsList = useCallback(
    (page: number) => {
      // The date range filter must be used as a pair. Reject a search that has
      // only one of the two dates, and an invalid range, with clear feedback.
      if (filters.startDate && !filters.endDate) {
        showToast("error", "Please also select an end date.");
        return;
      }
      if (!filters.startDate && filters.endDate) {
        showToast("error", "Please also select a start date.");
        return;
      }
      if (
        filters.startDate &&
        filters.endDate &&
        moment(filters.endDate).isBefore(moment(filters.startDate), "day")
      ) {
        showToast("error", "The end date must be on or after the start date.");
        return;
      }

      if (!loading) {
        setLoading(true);
        const params = {
          page,
          limit: 20,
        };

        if (filters.startDate) {
          params.startDate = moment(filters.startDate).format("YYYY-MM-DD");
        }
        if (filters.endDate) {
          params.endDate = moment(filters.endDate).format("YYYY-MM-DD");
        }

        getUserPaymentsListAction(params, dispatch)
          .then((res) => {
            if (res.type === "success") {
              setPagination(res.pagination);
            }
          })
          .catch((err) => {
            console.log("ERR: ", err);
          })
          .finally(() => setLoading(false));
      }
    },
    [filters, pagination]
  );

  useEffect(() => {
    console.log("paymentsList :", paymentsList);

    setPaymentsList(paymentsList);
  }, [paymentsList]);

  useEffect(() => {
    getPaymentsList(1);
  }, []);

  return (
    <PanelWrapper>
      <div className="container">
        <div className="page-header">
          <h2>Payments</h2>
        </div>

        <div className="page-content-card">
          <div className="content-card-header">
            <h3>List of payments</h3>

            <div className="filter-area">
              <div className="form-group">
                <DatePicker
                  showIcon
                  icon="far fa-calendar"
                  value={
                    filters.startDate
                      ? moment(filters.startDate).format("MM/DD/YYYY")
                      : ""
                  }
                  selected={filters.startDate}
                  className="form-control"
                  placeholderText="Start Date"
                  onChange={(date: Date | undefined) =>
                    onFilterChange("startDate", date)
                  }
                />
              </div>

              <div className="form-group">
                <DatePicker
                  showIcon
                  icon="far fa-calendar"
                  value={
                    filters.endDate
                      ? moment(filters.endDate).format("MM/DD/YYYY")
                      : ""
                  }
                  selected={filters.endDate}
                  className="form-control"
                  placeholderText="End Date"
                  onChange={(date: Date) => onFilterChange("endDate", date)}
                />
              </div>

              <CustomButton title="" onClick={() => getPaymentsList(1)}>
                <FontAwesomeIcon icon={faSearch} />
              </CustomButton>
            </div>
          </div>

          <div className="listing-tables">
            <Table responsive>
              <thead>
                <tr>
                  <th>Space Name</th>
                  <th>Amount</th>
                  <th>Payment Date</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th className="action-col">Action</th>
                </tr>
              </thead>

              <tbody>
                {!loading && payments.length === 0 && (
                  <tr>
                    <td colSpan={5} align="center">
                      No payments available
                    </td>
                  </tr>
                )}
                {payments.length > 0 &&
                  payments.map((item, index) => (
                    <tr key={`booking-${item.id}`}>
                      <td>{item?.space?.name || ""}</td>
                      <td>${item?.grossAmount}</td>
                      <td>
                        {item &&
                          item.createdAt &&
                          moment(item.createdAt).format("MM/DD/YYYY")}
                      </td>
                      <td className="text-capitalized">
                        {item?.status === "succeeded" && (
                          <span
                            className="status-icons text-success"
                            title="Success"
                          >
                            <FontAwesomeIcon icon={faCheckCircle} />
                          </span>
                        )}
                        {item?.status === "pending" && (
                          <span
                            className="status-icons text-warning"
                            title="Pending"
                          >
                            <FontAwesomeIcon icon={faClock} />
                          </span>
                        )}
                        {item?.status === "failed" && (
                          <span
                            className="status-icons text-danger"
                            title="Failed"
                          >
                            <FontAwesomeIcon icon={faCircleXmark} />
                          </span>
                        )}
                      </td>
                      <td className="action-col">
                        <ActionMenu
                          items={[
                            {
                              label: "Details",
                              icon: faChevronRight,
                              onClick: () =>
                                setRightSidePanel({ show: true, item }),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}

                {loading && (
                  <tr>
                    <td colSpan={5} align="center">
                      <FontAwesomeIcon icon={faSpinner} spin />
                    </td>
                  </tr>
                )}
              </tbody>

              <tfoot>
                <tr>
                  <td colSpan={5}>
                    <Pagination
                      pagination={pagination}
                      onPageChange={(page) => getPaymentsList(page)}
                    />
                  </td>
                </tr>
              </tfoot>
            </Table>
          </div>
        </div>
      </div>

      {rightSidePanel.show && (
        <RightSlidingPanel
          title={`Booking Info`}
          show={rightSidePanel.show}
          onClosePanel={() => setRightSidePanel({ show: false, item: null })}
        >
          <BookingInfoPanel
            item={{ id: rightSidePanel.item.bookingId }}
            listType="collected"
            closePanel={() => setRightSidePanel({ show: false, item: null })}
            showActionBtns="No"
          />
        </RightSlidingPanel>
      )}
    </PanelWrapper>
  );
};

export default MessagesPage;
